import { buildSearchParams, urlBuilder } from '@starjack/utils';
import { createEntityApi, type EntityEndpointBuilder } from '@starjack/api';
import {
    convertIssuedVoucherToFrontend,
    GetIssuedVouchersParams,
    GetIssuedVouchersResponse,
    IssueVoucherTemplateDTO,
    IssueVouchersCommand,
    IssuedVoucherBackendDTO,
    IssueVouchersResponseDTO,
} from '../model/issuedVoucher.types';

export const issuedVoucherUrls = {
    issuedVouchers: urlBuilder('order', '/admin/issued-vouchers'),
    templates: urlBuilder('order', '/admin/issued-vouchers/templates'),
    pdf: (id: string) =>
        `${urlBuilder('order', '/admin/issued-vouchers')}/${id}.pdf`,
    preview: (templateId: string) =>
        `${urlBuilder('order', '/vouchers')}/${templateId}/preview`,
};

export interface IssuedVoucherApiHooks {
    useGetIssueVoucherTemplatesQuery: (
        params?: void,
        options?: { skip?: boolean }
    ) => {
        data?: IssueVoucherTemplateDTO[];
        isFetching: boolean;
        isLoading: boolean;
        error?: unknown;
    };
    useGetIssuedVouchersQuery: (params: GetIssuedVouchersParams) => {
        data?: GetIssuedVouchersResponse;
        isFetching: boolean;
        isLoading: boolean;
        error?: unknown;
    };
    useIssueVouchersMutation: () => [
        (body: IssueVouchersCommand) => {
            unwrap: () => Promise<IssueVouchersResponseDTO>;
        },
        { isLoading: boolean; error?: unknown },
    ];
}

export const issuedVoucherEndpoints = (builder: EntityEndpointBuilder) => ({
    getIssueVoucherTemplates: builder.query<IssueVoucherTemplateDTO[], void>({
        query: () => issuedVoucherUrls.templates,
        providesTags: ['ISSUED_VOUCHERS'],
    }),

    getIssuedVouchers: builder.query<
        GetIssuedVouchersResponse,
        GetIssuedVouchersParams
    >({
        query: (params) =>
            `${issuedVoucherUrls.issuedVouchers}${buildSearchParams({
                code: params.code || undefined,
                page: params.page,
                size: params.size,
            })}`,
        transformResponse: (
            response: Omit<GetIssuedVouchersResponse, 'content'> & {
                content: IssuedVoucherBackendDTO[];
            }
        ): GetIssuedVouchersResponse => ({
            ...response,
            content: (response?.content ?? []).map(
                convertIssuedVoucherToFrontend
            ),
        }),
        providesTags: ['ISSUED_VOUCHERS'],
    }),

    issueVouchers: builder.mutation<
        IssueVouchersResponseDTO,
        IssueVouchersCommand
    >({
        query: (body) => ({
            url: issuedVoucherUrls.issuedVouchers,
            method: 'POST',
            body,
        }),
        transformResponse: (response: {
            vouchers?: IssuedVoucherBackendDTO[];
        }): IssueVouchersResponseDTO => ({
            vouchers: (response?.vouchers ?? []).map(
                convertIssuedVoucherToFrontend
            ),
        }),
        invalidatesTags: ['ISSUED_VOUCHERS'],
    }),
});

export const issuedVoucherApi = createEntityApi({
    tagTypes: ['ISSUED_VOUCHERS'],
    endpoints: issuedVoucherEndpoints,
});

export const useGetIssueVoucherTemplatesQuery: IssuedVoucherApiHooks['useGetIssueVoucherTemplatesQuery'] =
    issuedVoucherApi.useGetIssueVoucherTemplatesQuery;
export const useGetIssuedVouchersQuery: IssuedVoucherApiHooks['useGetIssuedVouchersQuery'] =
    issuedVoucherApi.useGetIssuedVouchersQuery;
export const useIssueVouchersMutation: IssuedVoucherApiHooks['useIssueVouchersMutation'] =
    issuedVoucherApi.useIssueVouchersMutation;
